import dayjs, { type Dayjs } from 'dayjs'
import { useFormik } from 'formik'
import {
  type FC,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, useParams } from 'react-router'

import { Icon } from '@iconify/react'
import { Modal } from 'common/components/Modal'
import { BEVERLY_HILLS_SECOND_PRODUCTION } from 'common/constants/siteSelectorConstants'
import { useApiResponse } from 'common/hooks/useApiResponse'
import { useAppSelector } from 'common/hooks/redux'
import type { ApiResponse } from 'common/interfaces/IRequestResponse'
import { INFO_CONSTANTS, PATIENT_STATUSES } from 'features/Home/constants/infoConstants'
import { NEW_PATIENT_FORM_KEYS } from 'features/Home/constants/infoNewPatientFormKeys'
import {
  type EHRAppointmentFormType,
  EHR_APPOINTMENT_SCHEMA,
} from 'features/Home/schemas/infoNewPatientSchema'
import type { PatientFormType } from 'features/Patients/components/PatientFormModal/PatientFormModal'
import type { IPatient } from 'features/Patients/interfaces/IPatient'
import { useCreatePatientMutation } from 'features/Patients/state/api/patientApi'
import type { AppointmentCategories } from 'features/Schedule/api/schedule.types'
import { useScheduleActions } from 'features/Schedule/pages/components/ScheduleDayView/hooks/useScheduleActions'
import { useScheduleWeekActions } from 'features/Schedule/pages/components/ScheduleWeekView/hooks/useScheduleWeekActions'
import { EScheduleView } from 'features/Schedule/pages/Schedule'
import { PathUtils } from 'routes/routes.utils'

import { EMPTY_ISSUES_SELECTION, type AppointmentIssuesSelection } from '../MedicalIssuesField'
import { useSaveAppointmentIssues } from '../MedicalIssuesField/useSaveAppointmentIssues'
import { AuditLines } from './AuditLines'
import {
  ChiefComplaintsField,
  COMPLAINT_TEXT,
  EMPTY_COMPLAINT_SNAPSHOT,
  type ComplaintListSnapshot,
  useSaveAppointmentComplaints,
} from './ChiefComplaintsField'
import { ClinicalPanel } from './ClinicalPanel'
import { DayPreview } from './DayPreview'
import { EditActionsBand } from './EditActionsBand'
import { EhrAppointmentModals } from './EhrAppointmentModals'
import styles from './ehrAppointmentV2.module.scss'
import {
  DEFAULT_WINDOW_DAYS,
  type ManageEhrAppointmentV2Props,
  type SearchCriteria,
  type SlotOption,
} from './ehrAppointmentV2.types'
import { FooterBar } from './FooterBar'
import { getInitialFormValues } from './initialValues'
import { IdentityRow } from './IdentityRow'
import { awayVisitOf } from './doctorAway'
import { canManualCheckIn } from './manualCheckIn'
import { formatDuration, formatMinutes, minutesFromDate, slotKey } from './pairing'
import { ResultsList } from './ResultsList'
import { SearchBar } from './SearchBar'
import { SmsStrip } from './SmsStrip'
import { useAppointmentActions } from './useAppointmentActions'
import { useAvailabilitySearch } from './useAvailabilitySearch'
import { useDialogMode } from './useDialogMode'
import { useDoctorAwayCheck } from './useDoctorAwayCheck'
import { useRoomFilter } from './useRoomFilter'
import { useSaveGuard } from './useSaveGuard'
import { useVisitRules } from './useVisitRules'
import { VisitRow, visitEndNote } from './VisitRow'

const DOCTOR_NA_CATEGORY = 'doctorna'

// Errors already shown next to their field; the footer only reports the rest.
const INLINE_ERROR_KEYS: ReadonlySet<string> = new Set([
  NEW_PATIENT_FORM_KEYS.FIRST_NAME,
  NEW_PATIENT_FORM_KEYS.LAST_NAME,
  NEW_PATIENT_FORM_KEYS.ROOM,
  NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES,
])

const roomHint = (
  { isActive, hasError, roomName }: ReturnType<typeof useRoomFilter>,
  label: string,
): string => {
  if (!isActive) return `${label}. Rooms are not checked yet.`
  if (hasError) return `${label}. ${roomName ?? 'The room'} could not be checked.`
  return `${label}, only when ${roomName ?? 'the room'} is free.`
}

const normalize = (value?: string): string =>
  value?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() ?? ''

export const ManageEhrAppointmentV2: FC<ManageEhrAppointmentV2Props> = memo((props) => {
  const {
    setOpen,
    open,
    isEditing,
    editedPatientData,
    title = INFO_CONSTANTS.ADD_WALK_IN_PATIENT,
    disabledEverything = false,
    customAppointment,
    date: selectedDate,
    languages,
    consultation_staffs,
    rooms: roomsData,
    schedules,
    disabledIntervals,
    dayInterval,
    timeZone,
    appointmentCategories,
    patientStatuses,
    scheduleView,
    punctuality = [],
    patientBufferTime = { after: 0, before: 0 },
    onDateChange,
    scheduleMonthData,
    hasEhrBooking,
  } = props

  const { isEmr, openingKey } = useDialogMode({
    open,
    appointmentId: editedPatientData?.id,
    hasEhrBooking,
  })

  const { siteId } = useParams()
  const { processApiResponse } = useApiResponse()
  const [createPatient] = useCreatePatientMutation()

  const scheduleActions =
    scheduleView === EScheduleView.WEEK ? useScheduleWeekActions() : useScheduleActions()
  const { isLoading } = scheduleActions
  const saveGuard = useSaveGuard()
  const isSaving = isLoading || saveGuard.isSaving

  const rooms = useMemo(() => roomsData.filter((room) => !!room.add_patient), [roomsData])
  const providers = useMemo(
    () => consultation_staffs?.filter((staff) => staff.is_provider || staff.is_doctor) ?? [],
    [consultation_staffs],
  )

  // Clears on reopen too: the Modal is destroyOnHidden and the field reports null on mount.
  const [complaintsLockLine, setComplaintsLockLine] = useState<string | null>(null)
  const [isSmsOpen, setIsSmsOpen] = useState(false)
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null)
  const [isAddPatientOpen, setIsAddPatientOpen] = useState(false)
  const [isPatientSearchOpen, setIsPatientSearchOpen] = useState(false)
  const [isNotifyOpen, setIsNotifyOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isEncounterOpen, setIsEncounterOpen] = useState(false)
  const [isCheckInOpen, setIsCheckInOpen] = useState(false)
  const [pendingOverlapSave, setPendingOverlapSave] = useState(false)

  const issuesRef = useRef<AppointmentIssuesSelection>(EMPTY_ISSUES_SELECTION)
  const { saveAppointmentIssues, saveCreatedAppointmentIssues } = useSaveAppointmentIssues({
    siteId,
    selectionRef: issuesRef,
  })

  const complaintsRef = useRef<ComplaintListSnapshot>(EMPTY_COMPLAINT_SNAPSHOT)
  const saveAppointmentComplaints = useSaveAppointmentComplaints({ siteId, listRef: complaintsRef })

  // Layout effect, so it runs before the OE fields' own effects write this opening's snapshot.
  useLayoutEffect(() => {
    complaintsRef.current = EMPTY_COMPLAINT_SNAPSHOT
    issuesRef.current = EMPTY_ISSUES_SELECTION
    setIsCheckInOpen(false)
  }, [openingKey])

  const actions = useAppointmentActions({
    siteId,
    editedPatientData,
    actions: scheduleActions,
    saveAppointmentIssues,
    saveCreatedAppointmentIssues,
    saveAppointmentComplaints,
    withClinical: isEmr,
  })

  const rules = useVisitRules({
    rooms,
    schedules,
    disabledIntervals,
    dayInterval,
    timeZone,
    editedAppointmentId: editedPatientData?.id,
  })

  // Recomputed only when the dialog reopens; a fresh object every render would reinitialise forever.
  const initialValues = useMemo(
    () =>
      getInitialFormValues({
        ...props,
        hasEhrBooking: isEmr,
        rooms,
        languages,
        consultationStaffs: consultation_staffs ?? [],
      }),
    [open, isEditing, editedPatientData?.id, selectedDate, customAppointment, isEmr],
  )

  const formik = useFormik<EHRAppointmentFormType>({
    initialValues,
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: EHR_APPOINTMENT_SCHEMA,
    onSubmit: () => void handleSave(),
  })

  const { values, errors, setFieldValue, setValues, resetForm } = formik

  const [criteria, setCriteria] = useState<SearchCriteria>({
    providerId: null,
    windowStart: dayjs(selectedDate).format('YYYY-MM-DD'),
    windowDays: DEFAULT_WINDOW_DAYS,
  })

  const search = useAvailabilitySearch({
    siteId,
    source: isEmr ? 'emr' : 'site',
    duration: values.duration ?? 15,
    categoryCode: isEmr ? Number(values.category_type_code) || undefined : undefined,
    emrAppointmentId: isEmr ? editedPatientData?.emr_appointment_id : undefined,
    criteria,
  })

  const activeTelehealthAppointmentId = useAppSelector(
    (state) => state.jitsiTelehealthReducer.activeSession?.appointmentId ?? null,
  )
  const previousTelehealthIdRef = useRef<number | null>(null)

  // When the call for this appointment ends, reflect the checked-out status like v1 did.
  useEffect(() => {
    const previousId = previousTelehealthIdRef.current
    previousTelehealthIdRef.current = activeTelehealthAppointmentId

    if (
      previousId !== null &&
      activeTelehealthAppointmentId === null &&
      previousId === editedPatientData?.id
    ) {
      setFieldValue(NEW_PATIENT_FORM_KEYS.PATIENT_STATUS, PATIENT_STATUSES.CHECKED_OUT)
    }
  }, [activeTelehealthAppointmentId, editedPatientData?.id])

  // The search provider is the appointment provider; one source of truth.
  useEffect(() => {
    if (values.consultation_staff && criteria.providerId !== values.consultation_staff) {
      setCriteria((current) => ({ ...current, providerId: values.consultation_staff }))
    }
  }, [values.consultation_staff])

  useEffect(() => {
    if (!open) return
    setIsSmsOpen(false)
    setSelectedSlotKey(null)
    setCriteria({
      providerId: initialValues.consultation_staff ?? null,
      windowStart: dayjs(initialValues.date ?? selectedDate).format('YYYY-MM-DD'),
      windowDays: DEFAULT_WINDOW_DAYS,
    })
    search.clear()

    // formik has not reinitialised yet, so seed from initialValues; values still holds the last appointment.
    if (initialValues.room) {
      const start = dayjs(initialValues.time).toDate()
      rules.selectRoom(
        String(initialValues.room),
        start,
        dayjs(start)
          .add(initialValues.duration ?? 0, 'minutes')
          .toDate(),
      )
    }
  }, [open, initialValues])

  const isCheckedOut = editedPatientData?.status?.code === PATIENT_STATUSES.CHECKED_OUT
  // The legacy reschedule lock is non-OE only; OE never had it.
  const isLocked = !isEmr && disabledEverything
  const isDisabled = isLocked || isCheckedOut
  const showCheckIn = !isEmr && canManualCheckIn(editedPatientData, timeZone)
  const patientName = values.first_name ? `${values.last_name}, ${values.first_name}` : ''
  const currentDay = dayjs(values.date ?? selectedDate).format('YYYY-MM-DD')
  // An unchanged edit, or a slot the calendar already warned about, is not asked again.
  const awayCheck = useDoctorAwayCheck({
    open,
    siteId,
    visit: awayVisitOf(values, selectedDate),
    acceptedVisit:
      isEditing || customAppointment?.awayConfirmed ? awayVisitOf(initialValues, selectedDate) : null,
  })
  // The seeded form time is site wall-clock digits whether the click handed over an instant or a display copy.
  const editedWindow = useMemo(() => {
    if (!editedPatientData) return undefined
    const start = dayjs(initialValues.time)
    return {
      day: editedPatientData.date,
      start: start.toDate(),
      end: start.add(initialValues.duration ?? 0, 'minutes').toDate(),
    }
  }, [editedPatientData, initialValues])
  const roomFilter = useRoomFilter({
    siteId,
    results: search.results,
    previewDay: currentDay,
    room: rooms.find((room) => String(room.value) === String(values.room)),
    editedId: editedPatientData?.id,
    // Wait for the EMR answer so the room check runs once, on the final set of days.
    enabled: search.hasSearched && !search.isFetching,
    searchId: search.searchId,
  })
  // Non-OE bookings carry the staff procedure v1 sent; OE records procedures in the problem list.
  const procedureFor = (staffId?: number) =>
    consultation_staffs?.find((staff) => staff.id === staffId)?.procedure
  const providerName =
    providers.find((provider) => provider.id === values.consultation_staff)?.name ?? 'Provider'

  const isDateDisabled = useCallback(
    (date: Dayjs) => scheduleMonthData?.[date.format('YYYY-MM-DD')]?.active === 0,
    [scheduleMonthData],
  )

  // Any change to when or where the visit sits re-runs the overlap and interval guards.
  const refreshGuards = (patch: { time?: Date; duration?: number; room?: string }) => {
    const start = patch.time ?? values.time
    const duration = patch.duration ?? values.duration ?? 0
    const end = dayjs(start).add(duration, 'minutes').toDate()

    formik.setFieldError('appointment', undefined)

    if (patch.room !== undefined) {
      rules.selectRoom(patch.room, start, end)
      return
    }

    // Fall back to the form's room so the guards still run if nothing selected one yet.
    const room =
      rules.selectedRoom ?? (values.room ? rules.selectRoom(String(values.room), start, end) : null)
    if (room) rules.updateOverlaps(room, start, end)
  }

  const changeCriteria = (patch: Partial<SearchCriteria>) => {
    if (patch.providerId !== undefined) {
      setFieldValue(NEW_PATIENT_FORM_KEYS.CONSULTATION_STAFF, patch.providerId)
    }
    setCriteria((current) => ({ ...current, ...patch }))
    setSelectedSlotKey(null)
  }

  const applySelection = (slot: SlotOption) => {
    setSelectedSlotKey(slotKey(slot))
    const time = dayjs(`${slot.day} 00:00`).add(slot.start, 'minutes').toDate()

    setValues({
      ...values,
      [NEW_PATIENT_FORM_KEYS.DATE]: slot.day,
      [NEW_PATIENT_FORM_KEYS.TIME]: time,
      [NEW_PATIENT_FORM_KEYS.DURATION]: slot.end - slot.start,
    } as unknown as EHRAppointmentFormType)

    refreshGuards({ time, duration: slot.end - slot.start })
    onDateChange?.(slot.day)
  }

  const applyCategory = (code: number) => {
    const category = appointmentCategories.find((item: AppointmentCategories) => item.code === code)
    const patch: Record<string, unknown> = {
      ...values,
      [NEW_PATIENT_FORM_KEYS.CATEGORY_TYPE_CODE]: code,
      [NEW_PATIENT_FORM_KEYS.DURATION]: category?.duration ?? 5,
      [NEW_PATIENT_FORM_KEYS.TITLE]: category?.name,
    }

    // Beverly Hills books "Doctor N/A" with no patient status.
    if (
      siteId === BEVERLY_HILLS_SECOND_PRODUCTION &&
      normalize(category?.name).includes(DOCTOR_NA_CATEGORY)
    ) {
      patch[NEW_PATIENT_FORM_KEYS.PATIENT_STATUS] = PATIENT_STATUSES.NONE
    }

    setValues(patch as unknown as EHRAppointmentFormType)
    refreshGuards({ duration: Number(patch[NEW_PATIENT_FORM_KEYS.DURATION]) })
  }

  const handlePatientSelect = (patient: IPatient) => {
    if (patient) {
      setValues({
        ...values,
        [NEW_PATIENT_FORM_KEYS.ID]: patient.id,
        [NEW_PATIENT_FORM_KEYS.FIRST_NAME]: patient.first_name,
        [NEW_PATIENT_FORM_KEYS.LAST_NAME]: patient.last_name,
        [NEW_PATIENT_FORM_KEYS.PHONE]: patient.phone,
        [NEW_PATIENT_FORM_KEYS.LANGUAGE]: patient.language_type_code,
        [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_TYPE_CODE]: 0,
        [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES]: 0,
        [NEW_PATIENT_FORM_KEYS.PATIENT_PUNCTUALITY]: {
          code: patient.punctuality_type_code,
          minutes: patient.punctuality_minutes,
        },
      } as unknown as EHRAppointmentFormType)
    }
    setIsPatientSearchOpen(false)
  }

  const handleSubmitPatientForm = async (patient: PatientFormType) => {
    const response: ApiResponse = await createPatient({
      first_name: patient.first_name,
      last_name: patient.last_name,
      phone: patient.phone,
      language_type_code: patient.language_type_code,
      doctor_emr_id: patient.doctor_emr_id,
    })

    processApiResponse(response, {
      success: 'Create patient success',
      error: 'Create patient error',
    })
    if (response?.data?.patient) handlePatientSelect(response.data.patient)
  }

  const close = () => {
    setOpen()
    resetForm()
    rules.reset()
    setComplaintsLockLine(null)
    setIsSmsOpen(false)
    setSelectedSlotKey(null)
    search.clear()
    document.querySelectorAll('.e-selected-cell')?.forEach((cell) => {
      cell.classList.remove('e-selected-cell')
    })
  }

  // Cancel, Esc and the X wait for a save in flight; the save closes the dialog itself.
  const requestClose = () => {
    if (saveGuard.isSavingRef.current) return
    close()
  }

  async function handleSave(force = false, awayAccepted = false) {
    if (isLoading) return
    if (!awayAccepted && !rules.isInsideDisabledInterval && awayCheck.requestConfirm()) return
    await saveGuard.run(() => saveAppointment(force))
  }

  // Non-OE saves send the status only when the user changed it, so a kiosk or SMS update survives.
  const omitStatus = !isEmr && values.patient_status === initialValues.patient_status

  async function saveAppointment(force: boolean) {
    if (rules.isInsideDisabledInterval) {
      formik.setFieldError('appointment', INFO_CONSTANTS.DISABLED_INTERVAL_WARNING)
      return
    }

    try {
      if (isEditing) {
        if (rules.overlappingEvents > 1 && !force) {
          setPendingOverlapSave(true)
          return
        }
        const procedure = isEmr ? undefined : editedPatientData?.procedure
        if (await actions.update(values, { procedure, omitStatus })) close()
        return
      }

      await actions.create(values, {
        issueIds: isEmr ? issuesRef.current.issueIds : [],
        complaints: isEmr ? complaintsRef.current.rows.map((row) => row.complaint) : [],
        procedure: isEmr ? undefined : procedureFor(values.consultation_staff),
      })
      close()
    } catch {
      // useScheduleActions already raised the notification; keep the dialog and its values.
    }
  }

  const handleDelete = async (sendSms: boolean) => {
    const deleted = await actions.remove(sendSms)
    setIsDeleteOpen(false)
    if (deleted) close()
  }

  const visitStart = minutesFromDate(dayjs(values.time).toDate())
  const appointmentError = errors.appointment ? String(errors.appointment) : undefined
  // Without this a schema failure on a field the dialog does not render makes Save look dead.
  const formError =
    appointmentError ??
    Object.entries(errors)
      .filter(([key, message]) => message && !INLINE_ERROR_KEYS.has(key))
      .map(([, message]) => String(message))[0]

  const footerTitle = `${formatMinutes(visitStart)} – ${formatMinutes(
    visitStart + (values.duration ?? 0),
  )} · ${formatDuration(values.duration ?? 0)} · ${providerName}`

  const selectedRoomName =
    rooms.find((room) => String(room.value) === String(values.room))?.short_name ??
    rooms.find((room) => String(room.value) === String(values.room))?.label

  const isLoadedDay = currentDay === dayjs(selectedDate).format('YYYY-MM-DD')

  const footerSubtitle = [
    dayjs(values.date ?? selectedDate).format('dddd, MMM D'),
    selectedRoomName ?? 'No room',
    isLoadedDay ? '' : 'conflicts not checked for this day',
  ]
    .filter(Boolean)
    .join(' · ')

  const clinicalPatientId = isEditing
    ? editedPatientData?.patient?.id ?? null
    : values.id
    ? Number(values.id)
    : null

  return (
    <>
      <Modal
        open={open}
        footer={null}
        width={1096}
        destroyOnHidden
        keyboard
        aria-label={title}
        wrapClassName={styles.root}
        classNames={{ content: styles.modal }}
        onCancel={requestClose}>
        <div className={styles.shell}>
          <div className={styles.header}>
            <h2 className={styles.headerTitle}>{title}</h2>
            {isEmr && isEditing && editedPatientData?.patient?.id && (
              <Link
                className={styles.headerLink}
                to={PathUtils.getCharts(
                  siteId,
                  editedPatientData.patient.id,
                  editedPatientData.emr_encounter_uuid,
                )}>
                {INFO_CONSTANTS.VIEW_IN_CHART}
                <Icon icon='gridicons:external' fontSize={16} />
              </Link>
            )}
            <span className={styles.headerSpacer} />
          </div>

          <IdentityRow
            patientName={patientName}
            phone={values.phone ?? ''}
            categoryCode={values.category_type_code}
            statusCode={values.patient_status}
            title={values.title ?? ''}
            categories={appointmentCategories}
            statuses={editedPatientData?.patient_statuses ?? patientStatuses}
            disabled={isDisabled}
            canPickPatient={!isEditing && !isCheckedOut}
            canAddPatient={!isEditing}
            patientError={errors.first_name ? 'Please select a patient' : undefined}
            onOpenPatientSearch={() => setIsPatientSearchOpen(true)}
            onAddPatient={() => setIsAddPatientOpen(true)}
            onCategoryChange={applyCategory}
            onStatusChange={(code) => setFieldValue(NEW_PATIENT_FORM_KEYS.PATIENT_STATUS, code)}
            onTitleChange={(next) => setFieldValue(NEW_PATIENT_FORM_KEYS.TITLE, next)}
          />

          <VisitRow
            date={dayjs(values.date ?? selectedDate)}
            time={dayjs(values.time)}
            duration={values.duration ?? 5}
            room={values.room}
            rooms={rooms}
            maxDuration={rules.calculateMaxDuration(values.time)}
            note={visitEndNote(dayjs(values.time), values.duration ?? 0)}
            disabled={isDisabled}
            showBypass={siteId !== BEVERLY_HILLS_SECOND_PRODUCTION}
            bypass={Boolean(values.temperature_bypass)}
            roomError={rules.roomErrorMessage ?? (errors.room ? String(errors.room) : undefined)}
            timeError={appointmentError}
            isDateDisabled={isDateDisabled}
            onDateChange={(date) => {
              const time = dayjs(values.time)
                .year(date.year())
                .month(date.month())
                .date(date.date())
                .toDate()
              setFieldValue(NEW_PATIENT_FORM_KEYS.DATE, date.format('YYYY-MM-DD'))
              setFieldValue(NEW_PATIENT_FORM_KEYS.TIME, time)
              onDateChange?.(date.format('YYYY-MM-DD'))
              setCriteria((current) => ({ ...current, windowStart: date.format('YYYY-MM-DD') }))
              refreshGuards({ time })
              setSelectedSlotKey(null)
            }}
            onTimeChange={(time) => {
              setFieldValue(NEW_PATIENT_FORM_KEYS.TIME, time.toDate())
              setSelectedSlotKey(null)
              refreshGuards({ time: time.toDate() })
            }}
            onDurationChange={(duration) => {
              setFieldValue(NEW_PATIENT_FORM_KEYS.DURATION, duration)
              refreshGuards({ duration })
            }}
            onRoomChange={(room) => {
              setFieldValue(NEW_PATIENT_FORM_KEYS.ROOM, room)
              refreshGuards({ room })
              setSelectedSlotKey(null)
            }}
            onBypassChange={(next) => setFieldValue(NEW_PATIENT_FORM_KEYS.TEMPERATURE_BYPASS, next)}
          />

          <ClinicalPanel
            comments={values.comments ?? ''}
            siteId={siteId}
            patientId={clinicalPatientId}
            appointmentId={editedPatientData?.id ?? null}
            mode={isEditing ? 'edit' : 'create'}
            disabled={isDisabled}
            selectionRef={issuesRef}
            onCommentsChange={(next) => setFieldValue(NEW_PATIENT_FORM_KEYS.COMMENTS, next)}
            commentsLockLine={complaintsLockLine}
            showProblemList={isEmr}
            complaints={
              isEmr ? (
                <ChiefComplaintsField
                  mode={isEditing ? 'edit' : 'create'}
                  siteId={siteId}
                  patientId={clinicalPatientId}
                  appointmentId={editedPatientData?.id ?? null}
                  disabled={isDisabled}
                  lockedReason={isCheckedOut ? COMPLAINT_TEXT.checkedOut : undefined}
                  listRef={complaintsRef}
                  onLockChange={setComplaintsLockLine}
                />
              ) : undefined
            }
          />

          <SmsStrip
            isOpen={isSmsOpen}
            punctuality={punctuality}
            patientPunctuality={values.patient_punctuality}
            typeCode={values.punctuality_type_code}
            minutes={values.punctuality_minutes ?? 0}
            bufferTime={patientBufferTime}
            disabled={isDisabled}
            error={errors.punctuality_minutes}
            onToggle={() => setIsSmsOpen((current) => !current)}
            onTypeChange={(value) =>
              setValues({
                ...values,
                [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_TYPE_CODE]: value,
                [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES]:
                  value === '0' ? 0 : values.punctuality_minutes,
              } as unknown as EHRAppointmentFormType)
            }
            onMinutesChange={(value) =>
              setFieldValue(NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES, value)
            }
            onReset={() =>
              setValues({
                ...values,
                [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_TYPE_CODE]: 0,
                [NEW_PATIENT_FORM_KEYS.PUNCTUALITY_MINUTES]: 0,
              } as unknown as EHRAppointmentFormType)
            }
          />

          <SearchBar
            criteria={criteria}
            providers={providers}
            isSearching={search.isFetching}
            canSearch={search.canSearch}
            isDisabled={isDisabled}
            isDateDisabled={isDateDisabled}
            onChange={changeCriteria}
            onSearch={search.run}
          />

          {search.hasSearched && (
            <div className={styles.results}>
              <ResultsList
                days={roomFilter.results}
                isFetching={search.isFetching || roomFilter.isFetching}
                hint={roomHint(roomFilter, isEmr ? 'Your EMR availability' : 'Your availability')}
                hasError={search.hasError}
                isUnreadable={search.isUnreadable}
                selectedKey={selectedSlotKey}
                isDisabled={isDisabled}
                onSelect={applySelection}
                onRetry={search.run}
              />
              <DayPreview
                siteId={siteId}
                day={currentDay}
                label={dayjs(currentDay).format('dddd, MMMM D, YYYY')}
                visitStart={visitStart}
                visitDuration={values.duration ?? 5}
                patientName={patientName}
                edited={editedWindow}
                timeZone={timeZone}
                roomBookings={roomFilter.previewBookings}
                roomName={roomFilter.roomName}
              />
            </div>
          )}

          {isEditing && editedPatientData?.id !== undefined && (
            <div className={styles.editMeta}>
              <EditActionsBand
                appointment={editedPatientData}
                encounterId={
                  isEmr
                    ? actions.mappedEncounterId ?? editedPatientData?.emr_encounter_id ?? null
                    : undefined
                }
                isBusy={isSaving}
                isNotifyLocked={actions.isNotifyLocked}
                isCheckedOut={isCheckedOut}
                onOpenEncounter={isEmr ? () => setIsEncounterOpen(true) : undefined}
                onManualCheckIn={showCheckIn ? () => setIsCheckInOpen(true) : undefined}
                onOpenNotify={() => setIsNotifyOpen(true)}
                onCallStarted={(statusTypeCode) =>
                  setFieldValue(NEW_PATIENT_FORM_KEYS.PATIENT_STATUS, statusTypeCode)
                }
              />
              <AuditLines appointment={editedPatientData} />
            </div>
          )}

          <FooterBar
            title={footerTitle}
            subtitle={footerSubtitle}
            error={formError}
            isSaving={isSaving}
            canSave={!rules.roomErrorMessage && !appointmentError && !isCheckedOut && !isLocked}
            canDelete={isEditing}
            hasSearched={search.hasSearched}
            onClearSearch={() => {
              search.clear()
              setSelectedSlotKey(null)
            }}
            onSave={() => formik.handleSubmit()}
            onDelete={() => setIsDeleteOpen(true)}
            onCancel={requestClose}
          />
        </div>
      </Modal>

      <EhrAppointmentModals
        languages={languages}
        isAddPatientOpen={isAddPatientOpen}
        isPatientSearchOpen={isPatientSearchOpen}
        isNotifyOpen={isNotifyOpen}
        isDeleteOpen={isDeleteOpen}
        isDeleting={actions.isDeleting}
        isEncounterOpen={isEncounterOpen}
        isResolvingEncounter={actions.isResolvingEncounter}
        encounterPatientId={editedPatientData?.patient?.id}
        selectedEncounterId={
          actions.mappedEncounterId ?? editedPatientData?.emr_encounter_id ?? null
        }
        encounterError={actions.encounterError}
        overlapConfirmOpen={pendingOverlapSave}
        onCloseAddPatient={() => setIsAddPatientOpen(false)}
        onSubmitPatient={handleSubmitPatientForm}
        onClosePatientSearch={() => setIsPatientSearchOpen(false)}
        onSelectPatient={handlePatientSelect}
        onCloseNotify={() => setIsNotifyOpen(false)}
        onConfirmNotify={async () => {
          setIsNotifyOpen(false)
          await actions.notify()
        }}
        onCancelDelete={() => setIsDeleteOpen(false)}
        onConfirmDelete={handleDelete}
        onCloseEncounter={() => setIsEncounterOpen(false)}
        onConfirmEncounter={async (params) => {
          const mapped = await actions.mapEncounter(params)
          if (mapped) setIsEncounterOpen(false)
        }}
        isManualCheckInOpen={isCheckInOpen && showCheckIn}
        onCloseManualCheckIn={() => setIsCheckInOpen(false)}
        onConfirmManualCheckIn={async () => {
          setIsCheckInOpen(false)
          if (await actions.checkIn()) close()
        }}
        awayConfirmOpen={awayCheck.isConfirmOpen}
        onCloseAwayConfirm={awayCheck.cancel}
        onConfirmAway={() => awayCheck.accept(() => void handleSave(false, true))}
        onCloseOverlapConfirm={() => setPendingOverlapSave(false)}
        onConfirmOverlap={() => {
          setPendingOverlapSave(false)
          void handleSave(true)
        }}
      />
    </>
  )
})
